import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Collapse
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { motion } from "framer-motion";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";

// Simple function to generate a random ID
const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

const RequestMovie = () => {
  const [name, setName] = useState("");
  const [request, setRequest] = useState("");
  const [requests, setRequests] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Commented out fetch requests functionality
  /*
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await axios.get("/api/movie-requests");
        if (response.data.success) {
          setRequests(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
        setError("Failed to load requests");
      }
    };
    fetchRequests();
  }, []);
  */

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !request.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setError(""); // Clear any previous errors
    setSuccessMessage(""); // Clear any previous success messages

    try {
      const response = await axios.post("/api/movie-requests", {
        name: DOMPurify.sanitize(name.trim()),
        movieRequest: DOMPurify.sanitize(request.trim())
      });
      
      if (response.data.success) {
        setRequests([...requests, response.data.data]);
        setName("");
        setRequest("");
        showSuccess("Request submitted successfully!");
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      const errorMessage = error.response?.data?.message || 
                          (error.response?.status === 429 
                            ? "Too many requests. Please wait a few minutes before trying again."
                            : "Failed to submit request. Please try again.");
      setError(errorMessage);
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleDelete = async (index, id) => {
    try {
      console.log('Attempting to delete request with ID:', id);
      const response = await axios.delete(`/api/movie-requests/${id}`);
      console.log('Delete response:', response.data);
      
      if (response.data.success) {
        const newRequests = requests.filter((req) => req._id !== id);
        setRequests(newRequests);
        showSuccess("Request deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting request:", error);
      setError(error.response?.data?.message || "Failed to delete request. Please try again.");
      setTimeout(() => setError(""), 5000);
    }
  };

  return (
    <Container maxWidth="md">
      <Helmet>
        <title>Request Movie-Latest South Hindi,Dubbed Movies</title>
        <link rel="canonical" href="https://moviehub888.xyz/request-movie" />
        <meta name="description" content="Request your favorite movies from South Indian, Bollywood, or Hollywood cinema. Get access to the latest movies in HD quality on MovieHub." />
        <meta name="keywords" content="request movies, latest south movies, bollywood movies, hollywood movies, HD movies, movie download" />
        <meta property="og:title" content="Request Movie-Latest South Hindi,Dubbed Movies" />
        <meta property="og:description" content="Request your favorite movies from South Indian, Bollywood, or Hollywood cinema. Get access to the latest movies in HD quality on MovieHub." />
        <meta property="og:url" content="https://moviehub888.xyz/request-movie" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          mt: 8,
          background: "linear-gradient(135deg, rgba(23, 32, 52, 0.95) 0%, rgba(26, 37, 60, 0.95) 100%)",
          borderRadius: "16px",
          padding: "2rem",
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)"
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            backgroundColor: "rgba(20, 30, 48, 0.95)",
            color: "white",
            borderRadius: 2,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            align="center"
            gutterBottom
            sx={{
              color: "#fff",
              fontWeight: "bold",
              mb: 4,
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
            }}
          >
            Request a Movie
          </Typography>

          <form onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              label="Your Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                form: {
                  autoComplete: 'off',
                },
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#4a90e2",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-focused": {
                    color: "#4a90e2",
                  },
                },
              }}
              required
            />

            <TextField
              fullWidth
              label="Movie Request"
              variant="outlined"
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              multiline
              rows={4}
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                form: {
                  autoComplete: 'off',
                },
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#4a90e2",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-focused": {
                    color: "#4a90e2",
                  },
                },
              }}
              required
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                py: 2,
                backgroundColor: "#4a90e2",
                backgroundImage: "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)",
                fontSize: "1.1rem",
                fontWeight: "bold",
                textTransform: "none",
                borderRadius: "12px",
                boxShadow: "0 4px 15px rgba(74, 144, 226, 0.3)",
                letterSpacing: "0.5px",
                "&:hover": {
                  backgroundColor: "#357abd",
                  backgroundImage: "linear-gradient(135deg, #5a9ee8 0%, #4a90e2 100%)",
                  boxShadow: "0 6px 20px rgba(74, 144, 226, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                  boxShadow: "0 2px 10px rgba(74, 144, 226, 0.3)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Submit Request
            </Button>
          </form>

          <Collapse in={!!(error || successMessage)}>
            <Box sx={{ mt: 2 }}>
              {successMessage && (
                <Alert 
                  severity="success"
                  sx={{
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    color: "#4caf50",
                    border: "1px solid #4caf50",
                  }}
                >
                  {successMessage}
                </Alert>
              )}
              
              {error && (
                <Alert 
                  severity="error"
                  sx={{
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                    color: "#ff4d4d",
                    border: "1px solid #ff4d4d",
                  }}
                >
                  {error}
                </Alert>
              )}
            </Box>
          </Collapse>

          {/* Commented out requests list section
          {requests.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: "white" }}>
                Your Requests
              </Typography>
              <List>
                {requests.map((req, index) => (
                  <ListItem
                    key={req._id}
                    sx={{
                      bgcolor: "rgba(74, 144, 226, 0.1)",
                      mb: 1,
                      borderRadius: 1,
                      border: "1px solid rgba(74, 144, 226, 0.2)",
                    }}
                  >
                    <ListItemText
                      primary={req.movieRequest}
                      secondary={`Requested by: ${req.name}`}
                      sx={{
                        "& .MuiListItemText-primary": {
                          color: "white",
                        },
                        "& .MuiListItemText-secondary": {
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(index, req._id)}
                        sx={{
                          color: "#ff4d4d",
                          "&:hover": {
                            backgroundColor: "rgba(255, 77, 77, 0.1)",
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          */}
        </Paper>
      </Box>
    </Container>
  );
};

export default RequestMovie;
